import { useContext, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import { SPAViewContext } from '@confluence/spa-view-context';

// TAP trait name and values
const LIVE_PAGES_BETA_TRAIT_NAME = 'confluence_live_pages_beta';
const LIVE_PAGES_BETA_DEFAULT_ON_VALUE = 'beta-default-on';
const LIVE_PAGES_BETA_DEFAULT_OFF_VALUE = 'beta-default-off';
export const LIVE_DOC_GA_EXP_TRAIT_NAME = 'confluence_modernize';
const LIVE_DOC_GA_EXP_TEST_GROUP_VALUE = 'exp-test';
const LIVE_DOC_GA_EXP_CONTROL_GROUP_VALUE = 'exp-control';

export enum LivePageReleasePhase {
	NOT_ENABLED = 'not_enabled',
	OPEN_BETA_INITIAL = 'open_beta_initial', // Tenant is in open beta, but not yet opted into live pages.
	OPEN_BETA_DEFAULT_ON = 'open_beta_default_on', // Tenant is in open beta and have opted into the open beta experience. Live docs are available and turned on by default is most space settings.
	OPEN_BETA_DEFAULT_OFF = 'open_beta_default_off', // Tenant is in open beta, and have opted out of the open beta experience. Live docs are still available, but will no longer be turned on by default in space settings.
	MODERNIZATION_GA_EXP_TEST = 'modernization_ga_exp_test',
	MODERNIZATION_GA_EXP_CONTROL = 'modernization_ga_exp_control',
}

const RELEASE_PHASES_WITH_FEATURE_ENABLED = [
	LivePageReleasePhase.OPEN_BETA_DEFAULT_ON,
	LivePageReleasePhase.OPEN_BETA_DEFAULT_OFF,
	LivePageReleasePhase.MODERNIZATION_GA_EXP_TEST,
];

// TODO: Cleanup
const RELEASE_PHASES_WITH_TWO_PAGE_TYPES_EXPERIENCE = [
	LivePageReleasePhase.OPEN_BETA_DEFAULT_ON,
	LivePageReleasePhase.OPEN_BETA_DEFAULT_OFF,
	LivePageReleasePhase.MODERNIZATION_GA_EXP_TEST,
];

const useLivePageReleasePhase = (): LivePageReleasePhase => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { experimentFeatures, loading: isSPAViewContextLoading } = useContext(SPAViewContext);

	useEffect(() => {
		if (isSPAViewContextLoading) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'traitValueLoading',
					actionSubject: 'useLivePageReleasePhase',
					source: 'useLivePageReleasePhase',
				},
			}).fire();
		}
	}, [createAnalyticsEvent, isSPAViewContextLoading]);

	let openBetaTraitValue;
	let modernizationGaTraitValue;
	try {
		const parsedExperimentFeatures = JSON.parse(experimentFeatures);
		if (typeof parsedExperimentFeatures === 'object') {
			({
				[LIVE_PAGES_BETA_TRAIT_NAME]: openBetaTraitValue,
				[LIVE_DOC_GA_EXP_TRAIT_NAME]: modernizationGaTraitValue,
			} = parsedExperimentFeatures);
		}
	} catch (e) {
		openBetaTraitValue = null;
		modernizationGaTraitValue = null;
	}

	// Modernization GA release phase
	/**
	 * Tenants are enrolled once they land in confluence.
	 *
	 * Tenants already in open beta or release tracks will not be part of the GA experiment.
	 * All other tenants will be split 50/50 between the test and control groups.
	 *
	 * Only the test group will have the modernization ga experience.
	 */
	if (modernizationGaTraitValue === LIVE_DOC_GA_EXP_TEST_GROUP_VALUE) {
		return LivePageReleasePhase.MODERNIZATION_GA_EXP_TEST;
	} else if (modernizationGaTraitValue === LIVE_DOC_GA_EXP_CONTROL_GROUP_VALUE) {
		return LivePageReleasePhase.MODERNIZATION_GA_EXP_CONTROL;
	}

	// Open Beta release phase
	/**
	 * `confluence_live_pages_open_beta` - Tells us that the tenant has been added to open beta. It allows the tenant to opt into the ope beta experience.
	 * This does not guarantee that the tenant has opted in. This is NOT a kill switch for the open beta experience. Once set to true, it cannot be set to false.
	 * `confluence_live_pages_open_beta_trait_opted_in` - Set to true when the tenant opts into the open beta experience. Will stay true even if the tenant opts out of the open beta experience.
	 * The meaning of this gate is that live docs are enabled for the tenant.
	 *
	 * The main trait value can have three states: null, beta-default-on, beta-default-off.
	 * 	- `null`: The tenant is not in open beta or has not yet opted into the open beta experience.
	 * 	- `beta-default-on`: The tenant has opted into the open beta experience and live docs are available
	 * 	- `beta-default-off`: The tenant has previously opted in and then opted out of the open beta experience.
	 * Live docs are still available but space settings have live docs turned off by default. User can still turn live docs back on in space settings.
	 *
	 * `confluence_live_pages_open_beta_trait_opted_in` is an abstraction of the trait value where it is true when the trait value is either `beta-default-on` or `beta-default-off`.
	 *
	 * We check both the fg and trait value to ensure the phase returned is accurate. Sometimes the feature gate fails to get the correct value.
	 * Currently, customers do not have the ability to opt out or completely opt out of the open beta experience once they have opted in.
	 * https://hello.atlassian.net/wiki/spaces/PCG/pages/4415395121/Live+Pages+Open+Beta+Feature+Gate+Approach
	 */
	if (
		openBetaTraitValue === LIVE_PAGES_BETA_DEFAULT_ON_VALUE ||
		fg('confluence_live_pages_open_beta_trait_opted_in') // we can assume that the tenant has opted in if the gate is true, opt out is not possible at the moment.
	) {
		return LivePageReleasePhase.OPEN_BETA_DEFAULT_ON;
	} else if (openBetaTraitValue === LIVE_PAGES_BETA_DEFAULT_OFF_VALUE) {
		return LivePageReleasePhase.OPEN_BETA_DEFAULT_OFF;
	} else if (fg('confluence_live_pages_open_beta')) {
		return LivePageReleasePhase.OPEN_BETA_INITIAL;
	}

	// Default
	return LivePageReleasePhase.NOT_ENABLED;
};

/**
 * Returns if the current tenant has the ability to use Live Pages.
 * This takes into account multiple releases that contribute to the feature's availability.
 */
export const useIsLivePagesFeatureEnabled = (): {
	isLivePagesFeatureEnabled: boolean;
	isTwoPageTypesExperience: boolean;
	livePageReleasePhase: LivePageReleasePhase;
} => {
	const phase = useLivePageReleasePhase();
	return {
		isLivePagesFeatureEnabled: RELEASE_PHASES_WITH_FEATURE_ENABLED.includes(phase),
		isTwoPageTypesExperience: RELEASE_PHASES_WITH_TWO_PAGE_TYPES_EXPERIENCE.includes(phase),
		livePageReleasePhase: phase,
	};
};
